import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import isToday from 'dayjs/plugin/isToday';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import arraySupport from 'dayjs/plugin/arraySupport';

import { RT_API_USAGE } from 'src/store/ActionTypes';
import * as api from 'src/store/api';
import * as actions from 'src/store/actions/rt_api_usage';

dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(isToday);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(arraySupport);
const selectEntries = (state) => state.rtApiUsage.entries;
const selectMasks = (state) => state.rtApiUsage.masks;
const selectClientBillingDay = (state) => state.app.client.billingDay;
const isMatchInWindow = (isMatch, inWindow) => isMatch && inWindow;

/**
 *
 * @return {CS}
 */
const selectConfig = (state) => state.rtApiUsage.config;

/**
 * fetchConfig - flow control for fetching a realtime config
 * @param {{type: string, payload: string }} action
 * @returns {Generator<*, void, *>}
 */
function* fetchConfig(action) {
  try {
    // NOTE: Check if the config has been set from the list page, and matches the requested config
    const { payload: configId } = action;
    /** @type {CS} config */
    let config = yield select(selectConfig);
    if ([null, undefined].includes(config)) {
      config = yield call(api.fetchRtConfig, configId);
    }
    if (config.score_type === 'static') config.config_metadata.model = { name: 'Test Environment', typeDetail: 'TEST' };
    else if (!config.config_metadata.model_id) {
      config.config_metadata.model = {
        id: -1 * Math.random(),
        name: config.config_metadata.name || 'CUSTOM MODEL',
        typeDetail: config.config_metadata.type_detail || 'CUSTOM TYPE',
        dataModelType: config.config_metadata.data_model_type || 'CLASSIFIER',
      };
    } else config.config_metadata.model = yield call(api.fetchModel, config.config_metadata.model_id);
    yield put({
      type: RT_API_USAGE.FETCH_CONFIG_SUCCEEDED,
      payload: config,
    });
  } catch (err) {
    yield put({
      type: RT_API_USAGE.FETCH_CONFIG_FAILURE,
      payload: err.message,
    });
  } finally {
    yield delay(500);
    yield put(actions.unset('fetchConfigError'));
  }
}

/**
 * generateStats - flow control for generating stat counts
 * @param {{type: string, payload: {countMatches: boolean} }} action
 * @returns {Generator<*, void, *>}
 */
function* generateStats(action) {
  try {
    /** @type {TrackingEntry[]} */
    const entries = yield select(selectEntries);
    const { ytdMatched, mtdMatched, tdyMatched, ytdTotal, mtdTotal, tdyTotal } = yield select(selectMasks);
    const { countMatches } = action.payload;
    const counts = {
      ytdCount: 0,
      mtdCount: 0,
      tdyCount: 0,
    };
    const ytdMask = countMatches ? ytdMatched : ytdTotal;
    const mtdMask = countMatches ? mtdMatched : mtdTotal;
    const tdyMask = countMatches ? tdyMatched : tdyTotal;
    entries.forEach((entry, idx) => {
      counts.ytdCount += ytdMask[idx] ? entry.count : 0;
      counts.mtdCount += mtdMask[idx] ? entry.count : 0;
      counts.tdyCount += tdyMask[idx] ? entry.count : 0;
    });
    yield put({
      type: RT_API_USAGE.GENERATE_STATS_SUCCEEDED,
      payload: counts,
    });
  } catch (err) {
    yield put({
      type: RT_API_USAGE.GENERATE_STATS_FAILED,
      payload: err.message,
    });
  } finally {
    yield delay(500);
    yield put(actions.unset('generateStatsError'));
  }
}

/**
 * fetchEntries - flow control for fetching a list of tracking entries
 * @param {{type: string, payload: string }} action
 * @returns {Generator<*, void, *>}
 */
function* fetchEntries(action) {
  try {
    const { payload: query } = action;
    const billingDay = yield select(selectClientBillingDay);
    const entries = yield call(api.fetchTrackingEntries, query);
    const masks = {
      ytdMatched: new Array(entries.length).fill(false),
      mtdMatched: new Array(entries.length).fill(false),
      btdMatched: new Array(entries.length).fill(false),
      tdyMatched: new Array(entries.length).fill(false),
      '7dMatched': new Array(entries.length).fill(false),
      '30dMatched': new Array(entries.length).fill(false),
      '1yMatched': new Array(entries.length).fill(false),
      '6cMatched': new Array(entries.length).fill(false),
      ytdUnmatched: new Array(entries.length).fill(false),
      mtdUnmatched: new Array(entries.length).fill(false),
      btdUnmatched: new Array(entries.length).fill(false),
      tdyUnmatched: new Array(entries.length).fill(false),
      '7dUnmatched': new Array(entries.length).fill(false),
      '30dUnmatched': new Array(entries.length).fill(false),
      '1yUnmatched': new Array(entries.length).fill(false),
      '6cUnmatched': new Array(entries.length).fill(false),
      ytdTotal: new Array(entries.length).fill(false),
      mtdTotal: new Array(entries.length).fill(false),
      btdTotal: new Array(entries.length).fill(false),
      tdyTotal: new Array(entries.length).fill(false),
      '7dTotal': new Array(entries.length).fill(false),
      '30dTotal': new Array(entries.length).fill(false),
      '1yTotal': new Array(entries.length).fill(false),
      '6cTotal': new Array(entries.length).fill(false),
    };
    // Generate the masks for entry filters
    // Walk the entries once, setting boolean masks as we go for each category
    entries.forEach((entry, idx) => {
      let billingWindowEnd = dayjs.utc([dayjs.utc().year(), dayjs.utc().month(), billingDay]);
      const isNewBillingPeriod = dayjs.utc().isSameOrAfter(billingWindowEnd);
      billingWindowEnd = isNewBillingPeriod ? billingWindowEnd : billingWindowEnd.subtract(1, 'month');
      const billingWindowStart = billingWindowEnd.subtract(6, 'month');
      const entryPeriod = dayjs.utc(entry.period, 'YYYY-MM-DD');
      const ytdCutoff = dayjs.utc([dayjs.utc().year(), 0]);
      const mtdCutoff = dayjs.utc([dayjs.utc().year(), dayjs.utc().month()]);
      const weekCutoff = dayjs.utc().subtract(7, 'day');
      const monthCutoff = dayjs.utc().subtract(30, 'day');
      const yearCutoff = dayjs.utc().subtract(1, 'year');
      const isMatch = entry.meta === 'matched';
      const entryToday = entryPeriod.isToday();
      const entryThisYear = entryPeriod.isSameOrAfter(ytdCutoff);
      const entryThisMonth = entryPeriod.isSameOrAfter(mtdCutoff);
      const entryThisBillingCycle = entryPeriod.isSameOrAfter(billingWindowEnd);
      const entryInWeekWindow = entryPeriod.isSameOrAfter(weekCutoff);
      const entryInMonthWindow = entryPeriod.isSameOrAfter(monthCutoff);
      const entryInYearWindow = entryPeriod.isSameOrAfter(yearCutoff);
      const entryInBillingWindow = entryPeriod.isBetween(billingWindowStart, billingWindowEnd, 'day', '[)');
      masks.ytdMatched[idx] = isMatchInWindow(isMatch, entryThisYear);
      masks.mtdMatched[idx] = isMatchInWindow(isMatch, entryThisMonth);
      masks.btdMatched[idx] = isMatchInWindow(isMatch, entryThisBillingCycle);
      masks.tdyMatched[idx] = isMatchInWindow(isMatch, entryToday);
      masks['7dMatched'][idx] = isMatchInWindow(isMatch, entryInWeekWindow);
      masks['30dMatched'][idx] = isMatchInWindow(isMatch, entryInMonthWindow);
      masks['1yMatched'][idx] = isMatchInWindow(isMatch, entryInYearWindow);
      masks['6cMatched'][idx] = isMatchInWindow(isMatch, entryInBillingWindow);
      masks.ytdUnmatched[idx] = isMatchInWindow(!isMatch, entryThisYear);
      masks.mtdUnmatched[idx] = isMatchInWindow(!isMatch, entryThisMonth);
      masks.btdUnmatched[idx] = isMatchInWindow(!isMatch, entryThisBillingCycle);
      masks.tdyUnmatched[idx] = isMatchInWindow(!isMatch, entryToday);
      masks['7dUnmatched'][idx] = isMatchInWindow(!isMatch, entryInWeekWindow);
      masks['30dUnmatched'][idx] = isMatchInWindow(!isMatch, entryInMonthWindow);
      masks['1yUnmatched'][idx] = isMatchInWindow(!isMatch, entryInYearWindow);
      masks['6cUnmatched'][idx] = isMatchInWindow(!isMatch, entryInBillingWindow);
      masks.ytdTotal[idx] = entryThisYear;
      masks.mtdTotal[idx] = entryThisMonth;
      masks.btdTotal[idx] = entryThisBillingCycle;
      masks.tdyTotal[idx] = entryToday;
      masks['7dTotal'][idx] = entryInWeekWindow;
      masks['30dTotal'][idx] = entryInMonthWindow;
      masks['1yTotal'][idx] = entryInYearWindow;
      masks['6cTotal'][idx] = entryInBillingWindow;
    });
    yield put({
      type: RT_API_USAGE.FETCH_ENTRIES_SUCCEEDED,
      payload: {
        entries,
        masks,
      },
    });
  } catch (e) {
    yield put({
      type: RT_API_USAGE.FETCH_ENTRIES_FAILED,
      payload: e.message,
    });
  } finally {
    yield delay(500);
    yield put(actions.unset('fetchEntriesError'));
  }
}

/**
 * fetchConfigList - flow control for fetching a list of configs
 * @param {{type: string, payload: string }} action
 * @returns {Generator<*, void, *>}
 */
function* fetchConfigList(action) {
  try {
    const { payload: query } = action;
    const configList = yield call(api.fetchRtConfigs, query);
    const modelList = configList.map((cs, idx) => {
      if (cs.score_type === 'static') return { name: 'Test Environment', typeDetail: 'TEST' };
      if (!cs.config_metadata.model_id) {
        return {
          id: -1 * Math.random(),
          name: cs.config_metadata.name || 'CUSTOM MODEL',
          typeDetail: cs.config_metadata.type_detail || 'CUSTOM TYPE',
          dataModelType: cs.config_metadata.data_model_type || 'CLASSIFIER',
        };
      }
      const options = {
        headers: {
          'X-Fields': 'name,dataModelType,predictionType,typeDetail',
        },
      };
      return call(api.fetchModel, cs.config_metadata.model_id, options);
    });
    const results = yield all(modelList);
    results.forEach((model, idx) => {
      configList[idx].config_metadata.model = model;
    });
    /** @param {CS} cs */
    yield put({
      type: RT_API_USAGE.FETCH_CONFIG_LIST_SUCCEEDED,
      payload: configList,
    });
  } catch (e) {
    yield put({
      type: RT_API_USAGE.FETCH_CONFIG_LIST_FAILURE,
      payload: e.message,
    });
  } finally {
    yield delay(500);
    yield put(actions.unset('fetchConfigListError'));
  }
}

function* generateStatsSaga() {
  yield takeLatest(RT_API_USAGE.GENERATE_STATS, generateStats);
}

function* fetchEntriesSaga() {
  yield takeLatest(RT_API_USAGE.FETCH_ENTRIES, fetchEntries);
}

function* fetchConfigSaga() {
  yield takeLatest(RT_API_USAGE.FETCH_CONFIG, fetchConfig);
}

function* fetchConfigListSaga() {
  yield takeLatest(RT_API_USAGE.FETCH_CONFIG_LIST, fetchConfigList);
}

export default [fetchEntriesSaga(), generateStatsSaga(), fetchConfigSaga(), fetchConfigListSaga()];
